import { conform, useForm } from '@conform-to/react'
import { getFieldsetConstraint, parse } from '@conform-to/zod'
import {
	json,
	redirect,
	type DataFunctionArgs,
	type MetaFunction,
} from '@remix-run/node'
import {
	Form,
	Link,
	useActionData,
	useLoaderData,
	useSearchParams,
} from '@remix-run/react'
import * as React from 'react'
import { ClientOnly } from 'remix-utils/client-only'
import { safeRedirect } from 'remix-utils/safe-redirect'
import { z } from 'zod'
import { Captcha } from '#app/components/captcha.client.tsx'
import {
	CheckboxField,
	ErrorList,
	ErrorListAlert,
	Field,
} from '#app/components/forms.tsx'
import { Spacer } from '#app/components/spacer.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { ReferralCategory } from '#app/services/referrer-data.ts'
import { giveReferralHisCreditsWithReferralCode } from '#app/services/referrer.server.ts'
import { requireAnonymous, sessionKey, signup } from '#app/utils/auth.server.ts'
import { checkCaptcha } from '#app/utils/captcha.server.ts'
import { redirectWithConfetti } from '#app/utils/confetti.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import * as gtag from '#app/utils/gtags.client.tsx'
import { getReferrerFromLocalStorage } from '#app/utils/local-storage.ts'
import { invariant, useIsPending } from '#app/utils/misc.tsx'
import { sessionStorage } from '#app/utils/session.server.ts'
import {
	NameSchema,
	PasswordSchema,
	UsernameSchema,
} from '#app/utils/user-validation.ts'
import { verifySessionStorage } from '#app/utils/verification.server.ts'
import { type VerifyFunctionArgs } from './verify.tsx'

const onboardingEmailSessionKey = 'onboardingEmail'

export const handle = {
	getSitemapEntries: () => null,
}

const SignupFormSchema = z
	.object({
		username: UsernameSchema,
		name: NameSchema,
		password: PasswordSchema,
		confirmPassword: PasswordSchema,
		agreeToTermsOfServiceAndPrivacyPolicy: z.boolean({
			required_error:
				'You must agree to the terms of service and privacy policy',
		}),
		remember: z.boolean().optional(),
		redirectTo: z.string().optional(),
		referredByCode: z.string().optional(),
	})
	.superRefine(({ confirmPassword, password }, ctx) => {
		if (confirmPassword !== password) {
			ctx.addIssue({
				path: ['confirmPassword'],
				code: 'custom',
				message: 'The passwords must match',
			})
		}
	})

async function requireOnboardingEmail(request: Request) {
	await requireAnonymous(request)
	const verifySession = await verifySessionStorage.getSession(
		request.headers.get('cookie'),
	)
	const email = verifySession.get(onboardingEmailSessionKey)
	if (typeof email !== 'string' || !email) {
		throw redirect('/signup')
	}
	return email
}
export async function loader({ request }: DataFunctionArgs) {
	const email = await requireOnboardingEmail(request)
	return json({ email })
}

export async function action({ request }: DataFunctionArgs) {
	const email = await requireOnboardingEmail(request)
	const formData = await request.formData()

	if (!(await checkCaptcha(request, formData))) {
		return json(
			{
				status: 'error',
				submission: null,
			} as const,
			{ status: 400 },
		)
	}

	const submission = await parse(formData, {
		schema: SignupFormSchema.superRefine(async (data, ctx) => {
			let existingUserWithUsername = await prisma.user.findUnique({
				where: { username: data.username },
				select: { id: true },
			})
			if (existingUserWithUsername) {
				ctx.addIssue({
					path: ['username'],
					code: z.ZodIssueCode.custom,
					message: 'A user already exists with this username',
				})
				return
			}

			let existingUserWithEmail = await prisma.user.findUnique({
				where: { email: email },
				select: { id: true },
			})

			if (existingUserWithEmail) {
				ctx.addIssue({
					path: ['email'],
					code: z.ZodIssueCode.custom,
					message:
						'A user already exists with the email you provided, please login instead.',
				})
				return
			}
		}),
		async: true,
	})

	if (submission.intent !== 'submit') {
		return json({ status: 'idle', submission } as const)
	}

	let hasError = Object.keys(submission.error).length > 0
	if (hasError || !submission.value) {
		return json({ status: 'error', submission } as const, { status: 400 })
	}

	let { remember, redirectTo, username, password, name, referredByCode } =
		submission.value
	const session = await signup({
		email,
		username,
		password,
		name,
		referredByCode,
	})
	if (referredByCode) {
		try {
			await giveReferralHisCreditsWithReferralCode(
				referredByCode,
				ReferralCategory.Login,
			)
		} catch (e) {
			console.error('Failed to give referral credits', e)
		}
	}

	const cookieSession = await sessionStorage.getSession(
		request.headers.get('cookie'),
	)
	cookieSession.set(sessionKey, session.id)
	const verifySession = await verifySessionStorage.getSession(
		request.headers.get('cookie'),
	)
	const headers = new Headers()
	headers.append(
		'set-cookie',
		await sessionStorage.commitSession(cookieSession, {
			expires: remember ? session.expirationDate : undefined,
		}),
	)
	headers.append(
		'set-cookie',
		await verifySessionStorage.destroySession(verifySession),
	)

	redirectTo = (redirectTo || '/me') + '?signupSuccess=true'
	return redirectWithConfetti(safeRedirect(redirectTo), { headers })
}

export async function handleVerification({
	request,
	submission,
}: VerifyFunctionArgs) {
	invariant(submission.value, 'submission.value should be defined by now')
	const verifySession = await verifySessionStorage.getSession(
		request.headers.get('cookie'),
	)
	verifySession.set(onboardingEmailSessionKey, submission.value.target)
	return redirect('/onboarding', {
		headers: {
			'set-cookie': await verifySessionStorage.commitSession(verifySession),
		},
	})
}

export const meta: MetaFunction = () => {
	return [{ title: 'Setup SwapMyFace Account' }]
}

export default function SignupRoute() {
	const data = useLoaderData<typeof loader>()
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')
	const [timestamp, setTimestamp] = React.useState(Date.now())

	const [form, fields] = useForm({
		id: 'signup-form',
		constraint: getFieldsetConstraint(SignupFormSchema),
		defaultValue: { redirectTo, referredByCode: getReferrerFromLocalStorage() },
		lastSubmission: actionData?.submission ?? undefined,
		onValidate({ formData }) {
			return parse(formData, { schema: SignupFormSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<div className="container flex min-h-full flex-col justify-center pb-32 pt-20">
			<div className="mx-auto w-full max-w-lg">
				<div className="flex flex-col gap-3 text-center">
					<h1 className="text-h1">Welcome aboard {data.email}!</h1>
					<p className="text-body-md text-muted-foreground">
						Please enter your details.
					</p>
				</div>
				<Spacer size="xs" />
				{actionData?.status === 'error' && actionData?.submission === null ? (
					<div className="mx-auto max-w-sm py-4 ">
						<ErrorListAlert
							errors={['The captcha verification failed']}
							elementClassName="text-md"
						/>
					</div>
				) : null}
				<Form
					method="POST"
					className="mx-auto min-w-[368px] max-w-sm"
					{...form.props}
					onSubmit={() => {
						setTimestamp(Date.now())
						gtag.event({
							action: 'sign_up',
							category: 'signup',
							label: 'sign up',
						})
					}}
				>
					<Field
						labelProps={{ htmlFor: fields.username.id, children: 'Username' }}
						inputProps={{
							...conform.input(fields.username),
							autoComplete: 'username',
							className: 'lowercase',
						}}
						errors={fields.username.errors}
					/>
					<Field
						labelProps={{ htmlFor: fields.name.id, children: 'Name' }}
						inputProps={{
							...conform.input(fields.name),
							autoComplete: 'name',
						}}
						errors={fields.name.errors}
					/>
					<Field
						labelProps={{ htmlFor: fields.password.id, children: 'Password' }}
						inputProps={{
							...conform.input(fields.password, { type: 'password' }),
							autoComplete: 'new-password',
						}}
						errors={fields.password.errors}
					/>

					<Field
						labelProps={{
							htmlFor: fields.confirmPassword.id,
							children: 'Confirm Password',
						}}
						inputProps={{
							...conform.input(fields.confirmPassword, { type: 'password' }),
							autoComplete: 'new-password',
						}}
						errors={fields.confirmPassword.errors}
					/>

					<CheckboxField
						labelProps={{
							htmlFor: fields.agreeToTermsOfServiceAndPrivacyPolicy.id,
							children: (
								<span>
									Do you agree to our{' '}
									<Link className="underline" to="/tos">
										Terms of Service
									</Link>{' '}
									and{' '}
									<Link className="underline" to="/privacy">
										Privacy Policy
									</Link>{' '}
									?
								</span>
							),
						}}
						buttonProps={conform.input(
							fields.agreeToTermsOfServiceAndPrivacyPolicy,
							{ type: 'checkbox' },
						)}
						errors={fields.agreeToTermsOfServiceAndPrivacyPolicy.errors}
					/>
					<CheckboxField
						labelProps={{
							htmlFor: fields.remember.id,
							children: 'Remember me',
						}}
						buttonProps={conform.input(fields.remember, { type: 'checkbox' })}
						errors={fields.remember.errors}
					/>

					<input
						{...conform.input(fields.referredByCode, { type: 'hidden' })}
					/>
					<input {...conform.input(fields.redirectTo, { type: 'hidden' })} />
					<ErrorList errors={form.errors} id={form.errorId} />
					{actionData?.submission?.error?.email && (
						<div className="pb-2">
							<ErrorListAlert errors={actionData?.submission?.error?.email} />
						</div>
					)}

					<div className="flex items-center justify-between gap-6">
						<StatusButton
							className="w-full"
							status={isPending ? 'pending' : actionData?.status ?? 'idle'}
							type="submit"
							disabled={isPending}
						>
							Create an account
						</StatusButton>
					</div>
					<div className="mt-6">
						<ClientOnly>{() => <Captcha rerender={timestamp} />}</ClientOnly>
					</div>
				</Form>
			</div>
		</div>
	)
}
